import { useEffect, useState } from 'react';
import { NumberInput } from '../../../components/NumberInput';
import { Loader2 } from 'lucide-react';
import api from '../../../lib/api';
import { fmtCLP } from '../egresos/egresosUtils';

const fechaCL = (iso) => (iso ? iso.split('-').reverse().join('-') : '');

// Facturas de venta con saldo del cliente: se eligen una o varias y el monto por factura (default: saldo).
// `objetivo` (abono de la cartola) permite marcar cuando la suma calza con el depósito.
export function FacturasCobroSelector({ clienteId, sel, onChange, objetivo }) {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clienteId) { setFacturas([]); return; }
    setLoading(true);
    api.get('/contabilidad/ingresos/facturas-abiertas', { params: { cliente_id: clienteId } })
      .then((r) => setFacturas(r.data.facturas || []))
      .catch(() => setFacturas([]))
      .finally(() => setLoading(false));
  }, [clienteId]);

  const toggle = (f) => {
    if (sel[f.alegra_id] !== undefined) {
      const rest = { ...sel };
      delete rest[f.alegra_id];
      onChange(rest);
    } else {
      const yaSuma = Object.values(sel).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const restante = objetivo ? Math.max(objetivo - yaSuma, 0) : null;
      const monto = restante !== null && restante > 0 && restante < f.saldo ? restante : f.saldo;
      onChange({ ...sel, [f.alegra_id]: monto });
    }
  };
  const suma = Object.values(sel).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  if (!clienteId) return <p className="rounded border border-dashed p-2 text-xs text-muted-foreground">Selecciona primero el cliente.</p>;
  if (loading) return <p className="flex items-center gap-2 p-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Buscando facturas con saldo…</p>;
  if (facturas.length === 0) return <p className="rounded border border-dashed p-2 text-xs text-muted-foreground" data-testid="facturas-cobro-empty">El cliente no tiene facturas con saldo en Alegra. Usa una cuenta contable (anticipo, otro ingreso).</p>;
  return (
    <div className="space-y-1" data-testid="facturas-cobro">
      <div className="max-h-48 space-y-1 overflow-y-auto pr-1">
        {facturas.map((f) => {
          const on = sel[f.alegra_id] !== undefined;
          return (
            <div key={f.alegra_id} className={`flex items-center gap-2 rounded-lg border p-2 text-xs ${on ? 'border-emerald-400 bg-emerald-50' : ''}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(f)} data-testid={`factura-cobro-${f.numero}`} />
              <span className="flex-1 truncate"><b>N°{f.numero}</b> · emitida {fechaCL(f.fecha)} · vence {fechaCL(f.fecha_vencimiento)} · saldo <span className="font-mono">{fmtCLP(f.saldo)}</span></span>
              {on && (
                <NumberInput value={sel[f.alegra_id]} onChange={(v) => onChange({ ...sel, [f.alegra_id]: v })} allowDecimal={false} allowNegative={false}
                  className="h-7 w-32 text-right font-mono text-xs" data-testid={`factura-cobro-monto-${f.numero}`} />
              )}
            </div>
          );
        })}
      </div>
      {Object.keys(sel).length > 0 && (
        <p className={`rounded px-2 py-1 text-xs font-medium ${objetivo && Math.abs(suma - objetivo) <= 0.5 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`} data-testid="facturas-cobro-suma">
          {Object.keys(sel).length} factura(s) · total {fmtCLP(suma)}
          {objetivo ? (Math.abs(suma - objetivo) <= 0.5 ? ' — calza con el abono ✓' : ` — abono ${fmtCLP(objetivo)} (diferencia ${fmtCLP(objetivo - suma)})`) : ''}
        </p>
      )}
    </div>
  );
}
